/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Flex, Image, VStack, Text, Input, Button, Alert } from '@chakra-ui/react';
import { useAuthStore } from '../store';
import { authService } from '../services';
import { hasPasswordConfirmationMismatch, passwordsMatch } from '../utils';

export const LoginPage: React.FC = () => {
  const [mode, setMode] = useState<'login' | 'forgot' | 'register' | 'invite'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteCompany, setInviteCompany] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteConfirmPassword, setInviteConfirmPassword] = useState('');
  const [inviteAccepted, setInviteAccepted] = useState(false);

  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const inviteToken = searchParams.get('inviteToken') ?? '';
  const inviteState = searchParams.get('invite');
  const verifiedState = searchParams.get('verified');
  const verifiedEmail = searchParams.get('email') ?? '';
  const redirectTo = (location.state as { from?: { pathname: string } })?.from?.pathname;

  useEffect(() => {
    if (inviteState === 'accepted') {
      setRegisterSuccess('Invite accepted. You can now sign in.');
      if (verifiedEmail) {
        setEmail(verifiedEmail);
      }
    }
    if (verifiedState === 'true') {
      setRegisterSuccess('Email verified. You can now sign in.');
      if (verifiedEmail) {
        setEmail(verifiedEmail);
      }
    }
    if (verifiedState === 'false') {
      setError('Email verification link is invalid or expired.');
    }
  }, [inviteState, verifiedEmail, verifiedState]);

  useEffect(() => {
    if (!inviteToken) {
      if (inviteState === 'expired') {
        setInviteError('Invite link is invalid or has already been used.');
      }
      return;
    }

    let cancelled = false;
    setInviteLoading(true);
    setInviteError('');
    setInviteAccepted(false);
    setMode('invite');

    authService
      .getInviteDetails(inviteToken)
      .then((details) => {
        if (cancelled) return;
        setInviteEmail(details.email);
        setInviteRole(details.role);
        setInviteName(details.name ?? '');
        setInviteCompany(details.company ?? '');
        setInvitePhone(details.phone ?? '');
      })
      .catch(() => {
        if (cancelled) return;
        setInviteError('Invite link is invalid or has already been used.');
      })
      .finally(() => {
        if (cancelled) return;
        setInviteLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [inviteState, inviteToken]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await authService.login({ email, password });
      setAuth(res.user, res.token);
      const fallbackTarget = res.user.role === 'demo_user' ? '/demo' : '/dashboard';
      const target =
        res.user.role === 'demo_user'
          ? redirectTo && redirectTo.startsWith('/demo')
            ? redirectTo
            : '/demo'
          : (redirectTo ?? fallbackTarget);
      navigate(target, { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid email or password.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setIsLoading(true);
    try {
      await authService.requestPasswordReset(forgotEmail);
      setForgotSent(true);
    } catch {
      // Show success regardless to avoid email enumeration
      setForgotSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword || !registerConfirmPassword) {
      setError('Name, email, and password are required.');
      return;
    }
    if (!passwordsMatch(registerPassword, registerConfirmPassword)) {
      setError('Password and confirmation do not match.');
      return;
    }
    setIsLoading(true);
    setError('');
    setRegisterSuccess('');

    try {
      await authService.register({
        name: registerName,
        email: registerEmail,
        password: registerPassword,
      });
      setRegisterSuccess('Account created. Check your email for verification, then sign in.');
      setEmail(registerEmail);
      setRegisterName('');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterConfirmPassword('');
      setMode('login');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteToken) {
      setInviteError('Invite link is invalid or has already been used.');
      return;
    }
    if (!inviteName.trim() || !invitePassword || !inviteConfirmPassword) {
      setInviteError('Name, password, and password confirmation are required.');
      return;
    }
    if (!passwordsMatch(invitePassword, inviteConfirmPassword)) {
      setInviteError('Password and confirmation do not match.');
      return;
    }

    setInviteLoading(true);
    setInviteError('');
    try {
      await authService.acceptInvite(inviteToken, {
        password: invitePassword,
        name: inviteName.trim(),
        company: inviteCompany.trim() || undefined,
        phone: invitePhone.trim() || undefined,
      });
      setInviteAccepted(true);
      setRegisterSuccess('Invite accepted. You can now sign in.');
      setEmail(inviteEmail);
      navigate(`/login?invite=accepted&email=${encodeURIComponent(inviteEmail)}`, { replace: true });
      setMode('login');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to accept invite.';
      setInviteError(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <Flex minH="100vh" bg="bg.canvas" align="center" justify="center">
      <Box bg="bg.card" borderRadius="12px" boxShadow="0 8px 32px rgba(0,0,0,0.12)" p={8} w="full" maxW="420px" mx={4}>
        {/* Logo */}
        <Flex align="center" gap={3} mb={8} justify="center">
          <Image src="/assets/logo-mark-dual.svg" alt="bidBrain" w="40px" h="40px" />
          <Text fontSize="xl" fontWeight="bold" color="fg.title">
            bidBrain Admin
          </Text>
        </Flex>

        {mode === 'login' ? (
          <>
            <Text fontSize="lg" fontWeight="600" color="fg.default" mb={6} textAlign="center">
              Sign in to your account
            </Text>

            {error && (
              <Alert.Root status="error" mb={4} borderRadius="8px">
                <Alert.Description fontSize="sm">{error}</Alert.Description>
              </Alert.Root>
            )}

            {registerSuccess && (
              <Alert.Root status="success" mb={4} borderRadius="8px">
                <Alert.Description fontSize="sm">{registerSuccess}</Alert.Description>
              </Alert.Root>
            )}

            <form onSubmit={handleLogin}>
              <VStack gap={4}>
                <Box w="full">
                  <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                    Email
                  </Text>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)' }}
                    autoComplete="email"
                  />
                </Box>
                <Box w="full">
                  <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                    Password
                  </Text>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)' }}
                    autoComplete="off"
                    name="admin_console_login_password"
                  />
                </Box>
                <Button
                  type="submit"
                  w="full"
                  bg="accent.solid"
                  color="white"
                  _hover={{ bg: 'accent.ink' }}
                  loading={isLoading}
                  loadingText="Signing in…"
                  mt={2}
                >
                  Sign In
                </Button>
              </VStack>
            </form>

            <Flex justify="center" mt={4}>
              <Button
                variant="ghost"
                size="sm"
                color="accent.solid"
                _hover={{ bg: 'accent.subtle' }}
                onClick={() => {
                  setError('');
                  setMode('forgot');
                }}
              >
                Forgot your password?
              </Button>
            </Flex>

            <Flex justify="center" mt={1}>
              <Button
                variant="ghost"
                size="sm"
                color="accent.solid"
                _hover={{ bg: 'accent.subtle' }}
                onClick={() => {
                  setError('');
                  setMode('register');
                }}
              >
                Create demo account
              </Button>
            </Flex>
          </>
        ) : mode === 'invite' ? (
          <>
            <Text fontSize="lg" fontWeight="600" color="fg.default" mb={2} textAlign="center">
              Accept your invite
            </Text>
            <Text fontSize="sm" color="fg.muted" mb={6} textAlign="center">
              Complete your profile and set your password to activate your account.
            </Text>

            {inviteError && (
              <Alert.Root status="error" mb={4} borderRadius="8px">
                <Alert.Description fontSize="sm">{inviteError}</Alert.Description>
              </Alert.Root>
            )}

            {inviteAccepted && (
              <Alert.Root status="success" mb={4} borderRadius="8px">
                <Alert.Description fontSize="sm">Invite accepted. Please sign in.</Alert.Description>
              </Alert.Root>
            )}

            {inviteLoading ? (
              <Text fontSize="sm" color="fg.muted" textAlign="center">
                Loading invite details…
              </Text>
            ) : (
              <form onSubmit={handleAcceptInvite}>
                <VStack gap={4}>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Email
                    </Text>
                    <Input value={inviteEmail} disabled />
                  </Box>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Assigned Role
                    </Text>
                    <Input value={inviteRole} disabled />
                  </Box>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Name
                    </Text>
                    <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Your name" />
                  </Box>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Company (optional)
                    </Text>
                    <Input
                      value={inviteCompany}
                      onChange={(e) => setInviteCompany(e.target.value)}
                      placeholder="Company"
                    />
                  </Box>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Phone (optional)
                    </Text>
                    <Input value={invitePhone} onChange={(e) => setInvitePhone(e.target.value)} placeholder="Phone" />
                  </Box>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Password
                    </Text>
                    <Input
                      type="password"
                      value={invitePassword}
                      onChange={(e) => setInvitePassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </Box>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Confirm Password
                    </Text>
                    <Input
                      type="password"
                      value={inviteConfirmPassword}
                      onChange={(e) => setInviteConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </Box>
                  {hasPasswordConfirmationMismatch(invitePassword, inviteConfirmPassword) && (
                    <Text fontSize="xs" color="error.solid" w="full">
                      Password and confirmation do not match.
                    </Text>
                  )}
                  <Button
                    type="submit"
                    w="full"
                    bg="accent.solid"
                    color="white"
                    _hover={{ bg: 'accent.ink' }}
                    loading={inviteLoading}
                    loadingText="Accepting invite…"
                    disabled={
                      !inviteName.trim() ||
                      !invitePassword ||
                      !inviteConfirmPassword ||
                      !passwordsMatch(invitePassword, inviteConfirmPassword)
                    }
                  >
                    Accept Invite
                  </Button>
                </VStack>
              </form>
            )}

            <Flex justify="center" mt={4}>
              <Button
                variant="ghost"
                size="sm"
                color="fg.muted"
                onClick={() => {
                  setMode('login');
                  setInviteError('');
                  navigate('/login', { replace: true });
                }}
              >
                ← Back to sign in
              </Button>
            </Flex>
          </>
        ) : mode === 'forgot' ? (
          <>
            <Text fontSize="lg" fontWeight="600" color="fg.default" mb={2} textAlign="center">
              Reset your password
            </Text>
            <Text fontSize="sm" color="fg.muted" mb={6} textAlign="center">
              Enter your email address and we'll send you a reset link.
            </Text>

            {forgotSent ? (
              <Alert.Root status="success" borderRadius="8px">
                <Alert.Description fontSize="sm">
                  If that email is registered, you'll receive a reset link shortly.
                </Alert.Description>
              </Alert.Root>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <VStack gap={4}>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                      Email
                    </Text>
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@example.com"
                      borderColor="border.default"
                      _focus={{
                        borderColor: 'accent.solid',
                        boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)',
                      }}
                    />
                  </Box>
                  <Button
                    type="submit"
                    w="full"
                    bg="accent.solid"
                    color="white"
                    _hover={{ bg: 'accent.ink' }}
                    loading={isLoading}
                    loadingText="Sending…"
                  >
                    Send Reset Link
                  </Button>
                </VStack>
              </form>
            )}

            <Flex justify="center" mt={4}>
              <Button
                variant="ghost"
                size="sm"
                color="fg.muted"
                onClick={() => {
                  setMode('login');
                  setForgotSent(false);
                }}
              >
                ← Back to sign in
              </Button>
            </Flex>
          </>
        ) : (
          <>
            <Text fontSize="lg" fontWeight="600" color="fg.default" mb={2} textAlign="center">
              Create your demo account
            </Text>
            <Text fontSize="sm" color="fg.muted" mb={6} textAlign="center">
              New self-service registrations are created with demo-user access.
            </Text>

            {error && (
              <Alert.Root status="error" mb={4} borderRadius="8px">
                <Alert.Description fontSize="sm">{error}</Alert.Description>
              </Alert.Root>
            )}

            <form onSubmit={handleRegister}>
              <VStack gap={4}>
                <Box w="full">
                  <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                    Name
                  </Text>
                  <Input
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    placeholder="Your name"
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)' }}
                  />
                </Box>
                <Box w="full">
                  <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                    Email
                  </Text>
                  <Input
                    type="email"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    placeholder="you@example.com"
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)' }}
                  />
                </Box>
                <Box w="full">
                  <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                    Password
                  </Text>
                  <Input
                    type="password"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    placeholder="••••••••"
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)' }}
                  />
                </Box>
                <Box w="full">
                  <Text fontSize="sm" fontWeight="500" color="fg.default" mb={1}>
                    Confirm Password
                  </Text>
                  <Input
                    type="password"
                    value={registerConfirmPassword}
                    onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 2px var(--chakra-colors-accent-subtle)' }}
                  />
                </Box>
                {hasPasswordConfirmationMismatch(registerPassword, registerConfirmPassword) && (
                  <Text fontSize="xs" color="error.solid" w="full">
                    Password and confirmation do not match.
                  </Text>
                )}
                <Button
                  type="submit"
                  w="full"
                  bg="accent.solid"
                  color="white"
                  _hover={{ bg: 'accent.ink' }}
                  loading={isLoading}
                  loadingText="Creating account…"
                  disabled={
                    !registerName ||
                    !registerEmail ||
                    !registerPassword ||
                    !registerConfirmPassword ||
                    !passwordsMatch(registerPassword, registerConfirmPassword)
                  }
                >
                  Register
                </Button>
              </VStack>
            </form>

            <Flex justify="center" mt={4}>
              <Button
                variant="ghost"
                size="sm"
                color="fg.muted"
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                ← Back to sign in
              </Button>
            </Flex>
          </>
        )}
      </Box>
    </Flex>
  );
};
